import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';

const heroImages: Record<string, string> = {
  'asmr-audio': 'https://readdy.ai/api/search-image?query=Soft%20dreamy%20abstract%20sound%20wave%20pattern%20in%20warm%20cream%20and%20beige%20tones%2C%20flowing%20curves%20suggesting%20audio%20frequencies%2C%20minimalist%20artistic%20composition%2C%20ethereal%20atmosphere%2C%20no%20text&width=1800&height=600&seq=cat-hero-asmr&orientation=landscape',
  'immersive-soundscape': 'https://readdy.ai/api/search-image?query=Misty%20forest%20landscape%20with%20soft%20morning%20light%2C%20dreamy%20ethereal%20atmosphere%2C%20warm%20cream%20and%20sage%20green%20tones%2C%20peaceful%20nature%20scene%2C%20fine%20art%20photography%2C%20no%20text&width=1800&height=600&seq=cat-hero-soundscape&orientation=landscape',
  'earphone-recommend': 'https://readdy.ai/api/search-image?query=Minimalist%20product%20photography%20of%20premium%20headphones%20on%20a%20clean%20wooden%20surface%2C%20soft%20warm%20side%20lighting%2C%20cream%20background%2C%20elegant%20composition%2C%20no%20text&width=1800&height=600&seq=cat-hero-earphone&orientation=landscape',
  'ersao-tips': 'https://readdy.ai/api/search-image?query=A%20serene%20person%20wearing%20headphones%20in%20a%20cozy%20minimalist%20room%20with%20soft%20warm%20sunlight%2C%20cream%20walls%2C%20peaceful%20meditation%20vibe%2C%20editorial%20interior%20photography%2C%20no%20text&width=1800&height=600&seq=cat-hero-tips&orientation=landscape',
  'sound-healing': 'https://readdy.ai/api/search-image?query=A%20peaceful%20zen%20space%20with%20a%20singing%20bowl%20and%20candles%2C%20soft%20warm%20glow%2C%20cream%20walls%2C%20minimalist%20healing%20atmosphere%2C%20fine%20art%20photography%2C%20no%20text&width=1800&height=600&seq=cat-hero-healing&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const heroImage = heroImages[category.slug] || heroImages['asmr-audio'];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 耳骚`,
    description: category.description,
    url: `https://ersao.fun/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '耳骚',
      url: 'https://ersao.fun/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
          <img
            src={heroImage}
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-50/20 text-background-50">
              <i className={`${category.icon} text-2xl`}></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-2">
              {category.name}
            </h1>
            <p className="text-sm text-background-200/80 max-w-lg mx-auto leading-relaxed">
              {category.description}
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: category.name },
                ]}
              />
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} category={category} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}